import { DialogObject } from '@/components/dialog/dialog.types';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import {
  CustomFieldProps,
  DynamicField,
  DynamicForm,
  FieldVariant,
  FileFieldProps,
  PasswordFieldProps,
} from '@/components/form-builder/form-builder.types';
import { StepperComponent } from '@/components/shared/stepper/stepper.component';
import { DacRepository } from '@/store/dac-state/dac-state.repository';
import { BehaviorSubject, map, of } from 'rxjs';
import { FileRemoveEvent, FileSelectEvent } from 'primeng/fileupload';
import { StepperBuilder } from '@/components/shared/stepper/stepper.types';
import { ButtonBlockProps } from '@/components/block-builder/block-builder.types';
import { PreviewPdfComponent } from '@/components/shared/preview-pdf/preview-pdf.component';

interface getDacUploadDialogProps {
  store: DacRepository;
  validate: () => void;
  passwordRequired?: boolean;
}

export const getDacUploadDialog = ({ store, validate, passwordRequired = false }: getDacUploadDialogProps): DialogObject => {
  const modelDacFile: DynamicField<FileFieldProps> = {
    label: 'Modèle DAC',
    variant: FieldVariant.FILE,
    isRequired: true,
    props: {
      mode: 'basic',
      accept: 'application/pdf',
      multiple: false,
      value: store.getNestedObservable<File[]>('upload', 'dacFile'),
      onFileSelect: (event: FileSelectEvent) => {
        store.setNested('upload', 'dacFile', event.currentFiles);
      },
      onFileRemoved: (event: FileRemoveEvent) => {
        store.setNested(
          'upload',
          'dacFile',
          store.getNested<File[]>('upload', 'dacFile').filter(file => file.name !== event.file.name)
        );
      },
      message: 'common.files.warn',
    },
  };

  const previewModelDacField: DynamicField<CustomFieldProps> = {
    label: 'dac.form.preview',
    variant: FieldVariant.CUSTOM,
    props: {
      component: PreviewPdfComponent,
      injectKeys: ['src', 'height'],
      src: store.getNestedObservable<File[]>('upload', 'dacFile').pipe(
        map(files => {
          const file = files?.[0];
          return file ? URL.createObjectURL(file) : '';
        })
      ),
      height: '400px',
    },
  };

  const modelAAOFile: DynamicField<FileFieldProps> = {
    label: 'Modèle AAO',
    variant: FieldVariant.FILE,
    isRequired: true,
    props: {
      mode: 'basic',
      accept: 'application/pdf',
      multiple: false,
      value: store.getNestedObservable<File[]>('upload', 'modelDacFile'),
      onFileSelect: (event: FileSelectEvent) => {
        store.setNested('upload', 'modelDacFile', event.currentFiles);
      },
      onFileRemoved: (event: FileRemoveEvent) => {
        store.setNested(
          'upload',
          'modelDacFile',
          store.getNested<File[]>('upload', 'modelDacFile').filter(file => file.name !== event.file.name)
        );
      },
      message: 'common.files.warn',
    },
  };

  const previewModelAAOField: DynamicField<CustomFieldProps> = {
    label: 'dac.form.preview',
    variant: FieldVariant.CUSTOM,
    props: {
      component: PreviewPdfComponent,
      injectKeys: ['src', 'height'],
      src: store.getNestedObservable<File[]>('upload', 'modelDacFile').pipe(
        map(files => {
          const file = files?.[0];
          return file ? URL.createObjectURL(file) : '';
        })
      ),
      height: '400px',
    },
  };

  const uploadFormObject: DynamicForm = {
    title: '',
    description: '',
    isHeaderHidden: true,
    class: '',
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          { fields: [modelDacFile, modelAAOFile] },
          {
            fields: [previewModelDacField, previewModelAAOField],
          },
        ],
      },
    ],
  };

  const useStepper = passwordRequired;

  if (!useStepper) {
    return {
      title: 'Téléversement',
      width: '60vw',
      actions: [
        {
          label: 'dac.actions.validate',
          variant: 'success',
          onClick: () => validate(),
        },
      ],
      component: {
        outlet: FormBuilderComponent,
        props: { formObject: uploadFormObject },
      },
    };
  }

  // Stepper mode: build steps dynamically
  const activeIndex$ = new BehaviorSubject<number>(1);

  const steps = [
    {
      value: 1,
      label: 'Téléversement',
      component: FormBuilderComponent,
      injectKeys: ['formObject'],
      formObject: uploadFormObject,
    },
  ];

  if (passwordRequired) {
    const passwordField: DynamicField<PasswordFieldProps> = {
      label: 'Mot de passe',
      variant: FieldVariant.PASSWORD,
      isRequired: true,
      error: store.getNestedObservable<string>('error', 'password'),
      props: {
        value: store.getNestedObservable<string>('upload', 'password'),
        onChange: (value: string) => {
          store.setNested('upload', 'password', value);
          store.setNested('error', 'password', '');
        },
        placeholder: 'Mot de passe',
        message: 'common.password.warn',
      },
    };

    const confirmPasswordField: DynamicField<PasswordFieldProps> = {
      label: 'Confirmer le mot de passe',
      variant: FieldVariant.PASSWORD,
      isRequired: true,
      error: store.getNestedObservable<string>('error', 'confirmPassword'),
      props: {
        value: store.getNestedObservable<string>('upload', 'confirmPassword'),
        onChange: (value: string) => {
          store.setNested('upload', 'confirmPassword', value);
          store.setNested('error', 'confirmPassword', '');
        },
        placeholder: 'Confirmer le mot de passe',
        message: 'common.password.warn',
      },
    };

    steps.push({
      value: steps.length + 1,
      label: 'Mot de passe',
      component: FormBuilderComponent,
      injectKeys: ['formObject'],
      formObject: {
        title: '',
        description: '',
        isHeaderHidden: true,
        grids: [
          {
            title: '',
            isHeaderHidden: true,
            gridItems: [{ fields: [passwordField] }, { fields: [confirmPasswordField] }],
          },
        ],
      },
    });
  }

  const stepperField: DynamicField<CustomFieldProps> = {
    label: '',
    variant: FieldVariant.CUSTOM,
    props: {
      component: StepperComponent,
      injectKeys: ['stepperBuilder', 'activeIndex'],
      activeIndex: activeIndex$.asObservable(),
      stepperBuilder: {
        classes: { content: 'max-h-[400px] overflow-auto px-2' },
        orientation: 'vertical',
        onActiveIndexChange: (index: number) => {
          const currentIndex = activeIndex$.getValue();

          // Validate current step before allowing forward navigation
          if (index > currentIndex) {
            const currentStep = steps[currentIndex - 1];

            if (currentStep.label === 'Mot de passe') {
              const password = store.getNested<string>('upload', 'password');
              if (!password) {
                store.setNested('error', 'password', 'Veuillez saisir un mot de passe');
                return;
              }
              const confirmPassword = store.getNested<string>('upload', 'confirmPassword');
              if (!confirmPassword) {
                store.setNested('error', 'confirmPassword', 'Veuillez confirmer le mot de passe');
                return;
              }
              if (password !== confirmPassword) {
                store.setNested('error', 'confirmPassword', 'Les mots de passe ne correspondent pas');
                return;
              }
              store.setNested('error', 'password', '');
              store.setNested('error', 'confirmPassword', '');
            }
          }

          activeIndex$.next(index);
        },
        items: steps,
        footerActions: activeIndex$.pipe(
          map(index => {
            const isLastStep = index === steps.length;

            return [
              {
                label: 'dac.actions.validate',
                onClick: () => {
                  validate();
                },
                severity: 'info',
                disabled: of(!isLastStep),
              } satisfies ButtonBlockProps,
            ];
          })
        ),
      } satisfies StepperBuilder,
    },
  };

  return {
    title: 'Téléversement',
    actions: !passwordRequired
      ? [
          {
            label: 'dac.actions.validate',
            variant: 'success',
            onClick: () => validate(),
          },
        ]
      : [],
    width: '60vw',
    component: {
      outlet: FormBuilderComponent,
      props: {
        formObject: {
          title: '',
          description: '',
          isHeaderHidden: true,
          grids: [
            {
              title: '',
              isHeaderHidden: true,
              gridItems: [{ fields: [stepperField] }],
            },
          ],
        },
      },
    },
  };
};
